import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/shared/lib/api";
import {
  Sparkles, Upload, Eye, Camera, FileText, Truck, AlertTriangle,
  CheckCircle2, ChevronRight, Sunrise, Download, IndianRupee, Palette, UserPlus, UserCheck, MessageSquareWarning,
} from "lucide-react";
import { toast } from "sonner";

/**
 * ActionQueueWidget — "What should I do now?" surface on the dashboard.
 *
 * Derives the user's pending tasks from project-state (not just notifications)
 * so first-time actions appear too. Each bucket is a category × count with
 * a list of tappable line-item rows that deep-link to the right tab.
 */
const BUCKET_ICON = {
  admin_create_client_login:    UserCheck,
  csm_assign_designer:          UserPlus,
  designer_share_concept:       Palette,
  designer_final_upload:        Upload,
  designer_rework:              AlertTriangle,
  prepress_review_final:        Eye,
  prepress_upload_production:   Upload,
  prepress_rework_production:   AlertTriangle,
  client_review_design:         Eye,
  client_review_production:     Eye,
  production_start:             CheckCircle2,
  production_download_file:     Download,
  csm_design_changes:           AlertTriangle,
  csm_relay_feedback:           MessageSquareWarning,
  ism_sign_dc:                  FileText,
  ism_install_photo:            Camera,
  accounts_bill:                Truck,
  accounts_bill_outsourced:     IndianRupee,
};

const TONE_CARD = {
  amber:   "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50/40",
  rose:    "border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50/40",
  blue:    "border-blue-200 bg-gradient-to-br from-blue-50 to-sky-50/40",
  emerald: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50/40",
  violet:  "border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50/40",
};
const TONE_BADGE = {
  amber:   "bg-amber-500 text-white",
  rose:    "bg-rose-500 text-white",
  blue:    "bg-blue-500 text-white",
  emerald: "bg-emerald-600 text-white",
  violet:  "bg-violet-600 text-white",
};
const TONE_ICON = {
  amber:   "text-amber-600",
  rose:    "text-rose-600",
  blue:    "text-blue-600",
  emerald: "text-emerald-600",
  violet:  "text-violet-600",
};

export default function ActionQueueWidget({ compact = false }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/my-action-queue")
      .then((r) => setData(r.data))
      .catch((e) => {
        // Non-fatal — widget just hides
        if (e.response?.status !== 401) {
          toast.error(fmtErr(e.response?.data?.detail) || "Couldn't load action queue");
        }
        setData({ buckets: [], total: 0 });
      });
  }, []);

  if (!data) {
    return (
      <div className="rounded-2xl border border-zinc-200 p-5 animate-pulse bg-zinc-50/40">
        <div className="h-4 bg-zinc-200 rounded w-1/3 mb-3" />
        <div className="h-3 bg-zinc-100 rounded w-2/3" />
      </div>
    );
  }

  if (data.total === 0 || !data.buckets?.length) {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-green-50/30 p-6 flex items-center gap-4"
           data-testid="action-queue-empty">
        <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
          <Sunrise className="w-6 h-6 text-emerald-700" />
        </div>
        <div>
          <div className="font-display font-bold text-zinc-900">You&apos;re all clear</div>
          <div className="text-sm text-zinc-600 mt-0.5">
            No pending actions assigned to you right now. Enjoy the calm.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="action-queue-widget">
      <div className="flex items-end justify-between mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-600">
            Up next
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Your action queue
          </h2>
        </div>
        <div className="text-right">
          <div className="font-display text-3xl font-bold tabular-nums">{data.total}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            pending
          </div>
        </div>
      </div>

      <div className={`grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-1 lg:grid-cols-2"}`}>
        {data.buckets.map((b) => (
          <BucketCard key={b.key} bucket={b} />
        ))}
      </div>
    </div>
  );
}

function BucketCard({ bucket }) {
  const Icon = BUCKET_ICON[bucket.key] || CheckCircle2;
  const cardCls = TONE_CARD[bucket.tone] || TONE_CARD.amber;
  const badgeCls = TONE_BADGE[bucket.tone] || TONE_BADGE.amber;
  const iconCls = TONE_ICON[bucket.tone] || TONE_ICON.amber;
  const [expanded, setExpanded] = useState(true);

  return (
    <div className={`rounded-2xl border ${cardCls} overflow-hidden`}
         data-testid={`bucket-${bucket.key}`}>
      <button onClick={() => setExpanded((s) => !s)}
              className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-white/40 transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center ${iconCls}`}>
            <Icon className="w-5 h-5" />
          </div>
          <div className="text-left">
            <div className="font-display font-bold text-zinc-900">{bucket.label}</div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {bucket.count} {bucket.count === 1 ? "item" : "items"} · tap to {expanded ? "collapse" : "expand"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 h-7 rounded-full text-xs font-mono font-semibold inline-flex items-center ${badgeCls}`}>
            {bucket.count}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="bg-white/60 backdrop-blur-sm divide-y divide-zinc-100">
          {bucket.items.map((it, i) => (
            <Link key={`${it.project_code}-${it.boq_id || it.challan_number || i}`}
                  to={it.link}
                  data-testid={`bucket-item-${bucket.key}-${i}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-orange-50/40 transition-colors group">
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                  {it.project_code}
                </div>
                <div className="text-sm font-medium text-zinc-900 truncate mt-0.5">
                  {it.project_name || "—"}
                </div>
                <div className="text-xs text-zinc-700 mt-1 truncate">
                  <span className="font-medium">{it.label}</span>
                  {it.context && <span className="text-zinc-500"> · {it.context}</span>}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-zinc-300 group-hover:text-orange-500 transition-colors flex-shrink-0" />
            </Link>
          ))}
          {bucket.count > bucket.items.length && (
            <div className="px-4 py-2 text-[11px] font-mono text-zinc-500 italic">
              +{bucket.count - bucket.items.length} more — open the project to see all
            </div>
          )}
        </div>
      )}
    </div>
  );
}
